import classesK from '../kalkulatori.module.css'
import InfoBar from '../redesign/components/InfoBar'
import OddsCalculator from '../redesign/components/OddsCalculator'
import ResultsSection from '../redesign/components/ResultsSection'
import { useEffect, useState } from 'react'
import resultSectionsData from './ProjekcijaUtakmiceResultsData'
import runCalculator from '../api/runCalculator'

const matchMetrics = [
  'shots',
  'shotsOnTarget',
  'shotsOffTarget',
  'blockedShots',
  'freeKicks',
  'corners',
  'offsides',
  'crosses',
  'goalkeeperSaves',
  'fouls',
  'yellowCards',
  'passes',
  'accuratePasses',
  'tackles',
  'attacks',
  'dangerousAttacks',
]

const generateMetricTemplate = () => ({
  total: {
    firstHalf: '',
    fullTime: '',
  },
  home: {
    firstHalf: '',
    fullTime: '',
  },
  away: {
    firstHalf: '',
    fullTime: '',
  },
})

const calculatorType = 'Projekcija utakmice'

const ProjekcijaUtakmice = () => {
  const [disabled, setDisabled] = useState(true)
  const [showInputArea, setShowInputArea] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const generateMetricsState = () => {
    const metrics = {}
    matchMetrics.forEach((metric) => {
      metrics[metric] = generateMetricTemplate()
    })
    return metrics
  }

  const getInitialData = () => ({
    values: {
      matchOutcomeOdds: ['', '', ''], // KI 1, KI X, KI 2
      goalsOdds: ['', ''], // UG 0-2, UG 3+
      teamStats: ['', '', '', '', '', ''], // DOM+, DOM-, DOM BR., GOST-, GOST+, GOST BR.
    },
    results: {
      xG: {
        total: '',
        firstHalf: '',
        secondHalf: '',
      },

      possession: {
        home: '',
        away: '',
      },

      ...generateMetricsState(),

      teamStrength: {
        home: {
          attack: '',
          defense: '',
        },
        away: {
          attack: '',
          defense: '',
        },
        combined: {
          attack: '',
          defense: '',
        },
        matchup: {
          homeAttack_vs_awayDefense: '',
          homeDefense_vs_awayAttack: '',
        },
      },
    },
  })

  const [data, setData] = useState(getInitialData)

  const getResultsFromKey = (results, key, subtitle) => {
    try {
      if (key === 'teamStrength') {
        switch (subtitle) {
          case 'Snaga domaćina':
            return [
              results.teamStrength.home.attack,
              results.teamStrength.home.defense,
            ]
          case 'Snaga gosta':
            return [
              results.teamStrength.away.attack,
              results.teamStrength.away.defense,
            ]
          case 'Snage oba tima zajedno':
            return [
              results.teamStrength.combined.attack,
              results.teamStrength.combined.defense,
            ]
          case 'Tim proseci odbrana i napada':
            return [
              results.teamStrength.matchup.homeAttack_vs_awayDefense,
              results.teamStrength.matchup.homeDefense_vs_awayAttack,
            ]
          default:
            return []
        }
      }

      // for metrics with nested structure
      if (matchMetrics.includes(key)) {
        const metric = results[key]
        return [
          metric.total.firstHalf,
          metric.total.fullTime,
          metric.home.fullTime,
          metric.home.firstHalf,
          metric.home.fullTime - metric.home.firstHalf,
          metric.away.fullTime,
          metric.away.firstHalf,
          metric.away.fullTime - metric.away.firstHalf,
        ]
      }

      // for simple structures
      return Object.values(results[key])
    } catch (e) {
      console.log(key, e)
    }
  }

  // determine if button is disabled
  useEffect(() => {
    const { matchOutcomeOdds, goalsOdds, teamStats } = data.values

    const allFilled = [...matchOutcomeOdds, ...goalsOdds, ...teamStats].every(
      (val) => val !== ''
    )

    setDisabled(!allFilled)
  }, [data.values])

  const clearFields = () => {
    setData(getInitialData())
    setShowInputArea(true)
    setError('')
  }

  const handleButtonClick = async () => {
    if (!showInputArea) {
      clearFields()
      setDisabled(true)
      return
    }

    const ok = await handleCalculate()
    if (ok) {
      setShowInputArea(false)
    }
  }

  const buildBody = () => {
    const { matchOutcomeOdds, goalsOdds, teamStats } = data.values

    return {
      matchOutcomeOdds: matchOutcomeOdds.map((v) => parseFloat(v)),
      goalsOdds: goalsOdds.map((v) => parseFloat(v)),
      teamStats: teamStats.map((v) => parseFloat(v)),
    }
  }

  const handleCalculate = async () => {
    setLoading(true)
    setError('')

    try {
      const results = await runCalculator(
        'statistics',
        'match-projection',
        buildBody()
      )

      setData((prev) => ({
        ...prev,
        results: {
          ...prev.results,
          ...results,
        },
      }))

      return true
    } catch (e) {
      setError('Greška pri obračunu, pokušajte ponovo.')
      return false
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={classesK.container}>
      <InfoBar calculatorType={calculatorType} onRefresh={clearFields} />

      <div className={classesK.contentContainer}>
        <div className={classesK.submitWrapper}>
          <span>Unesi nove obračunske podatke</span>
          <button onClick={handleButtonClick} disabled={disabled || loading}>
            {showInputArea
              ? loading
                ? 'Obračun u toku...'
                : 'Prikaži rezultate'
              : 'Unesi obračunske podatke'}
          </button>
        </div>
        {error && <div className={classesK.errorMessage}>{error}</div>}
        {showInputArea && (
          <>
            <div className={classesK.sectionTitle}>
              Upisi kvote za ishod utakmice
            </div>
            <OddsCalculator
              field={'matchOutcomeOdds'}
              data={data.values}
              setData={setData}
              labelPrefix={['KI 1', 'KI X', 'KI 2']}
              step={['0.1', '0.1', '0.1']}
              min={['0.1', '0.1', '0.1']}
              type={['number', 'number', 'number']}
              rows={3}
            />

            <div className={classesK.sectionTitle}>Upiši kvote za golove</div>
            <OddsCalculator
              field={'goalsOdds'}
              data={data.values}
              setData={setData}
              labelPrefix={['Manje od 2.5 ( UG 0-2 )', 'Više od 2.5 ( UG 3+ )']}
              rows={2}
              type={['number', 'number']}
              step={['0.1', '0.1']}
              min={['0.1', '0.1']}
            />

            <div className={classesK.sectionTitle}>
              Upiši br. golova / br. ut. za oba tima
            </div>
            <OddsCalculator
              field={'teamStats'}
              data={data.values}
              setData={setData}
              labelPrefix={[
                'DOM +  ( dom. dao golova)',
                'DOM -  ( dom. primio golova)',
                'DOM BR. ( dom. odigrao utakmica)',
                'GOST +  ( gost. dao golova)',
                'GOST -  ( gost. primio golova)',
                'GOST BR. ( gost. odigrao utakmica)',
              ]}
              type={[
                'number',
                'number',
                'number',
                'number',
                'number',
                'number',
              ]}
              step={['1', '1', '1', '1', '1', '1']}
              min={['0', '0', '1', '0', '0', '1']}
              rows={3}
            />
          </>
        )}

        {!showInputArea && (
          <div className={classesK.sectionResults}>
            {resultSectionsData.map(
              ([title, subtitle, key, names, layoutPattern], index) => (
                <ResultsSection
                  key={index}
                  title={title}
                  subtitle={subtitle}
                  results={getResultsFromKey(data.results, key, subtitle)}
                  names={names}
                  layoutPattern={layoutPattern}
                />
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjekcijaUtakmice
